import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Copy, Linkedin, Share2, Loader2 } from "lucide-react";
import { drawShareCard } from "./toolkit";

export default function ShareDialog({ open, onOpenChange, card }) {
  const [img, setImg] = useState(null);
  const [caption, setCaption] = useState(card.caption);
  const url = `${window.location.origin}/tools${card.slug ? `/${card.slug}` : ""}`;
  const fileName = `ruleai-${card.slug || "health-score"}.png`;

  useEffect(() => {
    if (!open) return;
    setImg(null);
    setCaption(card.caption);
    drawShareCard(card).then(setImg);
  }, [open, card]);

  const blob = async () => (await fetch(img)).blob();
  const download = () => {
    const a = document.createElement("a");
    a.href = img; a.download = fileName; a.click();
  };
  const copyImage = async () => {
    try {
      await navigator.clipboard.write([new ClipboardItem({ "image/png": await blob() })]);
      toast.success("Image copied — paste it into Slack or LinkedIn");
    } catch { toast.error("Copy not supported here — use Download instead"); }
  };
  const copyCaption = async () => {
    try { await navigator.clipboard.writeText(`${caption}\n${url}`); toast.success("Caption copied"); }
    catch { toast.error("Could not copy"); }
  };
  const linkedin = () => {
    copyCaption();
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, "_blank", "noopener");
  };
  const nativeShare = async () => {
    const file = new File([await blob()], fileName, { type: "image/png" });
    if (navigator.canShare?.({ files: [file] })) await navigator.share({ files: [file], text: `${caption}\n${url}` }).catch(() => {});
    else download();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="dark bg-card text-foreground border-border max-w-2xl" data-testid="share-dialog">
        <DialogHeader><DialogTitle className="font-display">Share your result</DialogTitle></DialogHeader>
        <div className="aspect-[1200/627] w-full border border-border rounded-sm bg-background grid place-items-center overflow-hidden">
          {img ? <img src={img} alt="Share card" className="w-full h-full object-contain" data-testid="share-card-image" />
            : <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />}
        </div>
        <textarea
          value={caption} onChange={(e) => setCaption(e.target.value)} rows={2}
          className="w-full border border-input bg-background rounded-sm px-3 py-2 text-sm outline-none focus:border-primary"
          data-testid="share-caption"
        />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Button onClick={download} disabled={!img} className="rounded-sm" data-testid="share-download-btn"><Download className="w-4 h-4 mr-2" />Download</Button>
          <Button onClick={copyImage} disabled={!img} variant="outline" className="rounded-sm" data-testid="share-copy-image-btn"><Copy className="w-4 h-4 mr-2" />Copy image</Button>
          <Button onClick={linkedin} variant="outline" className="rounded-sm" data-testid="share-linkedin-btn"><Linkedin className="w-4 h-4 mr-2" />LinkedIn</Button>
          <Button onClick={nativeShare} disabled={!img} variant="outline" className="rounded-sm" data-testid="share-native-btn"><Share2 className="w-4 h-4 mr-2" />More</Button>
        </div>
        <p className="text-xs text-muted-foreground">LinkedIn opens with the tool link; your caption is copied — attach the downloaded image to your post.</p>
      </DialogContent>
    </Dialog>
  );
}
